// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonVariants } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { boxStyles } from "@store/Themes/common/mixins/box";
import { contentSpacingStyles } from "@store/Themes/common/mixins/spacing";
import { documentsListStyles } from "@store/Themes/common/props/document";
import { ToolkitFieldDefinition } from "@toolkit/Framework/Types/FieldDefinition";
import React, { FC } from "react";
import { css } from "styled-components";

interface RowModel {
    fields: {
        description: string;
        documentType: string;
        filePath: string;
        id: string;
        name: string;
        ellipsis: boolean;
    };
}

const enum fields {
    hasMultipleFiles = "hasMultipleFiles",
    rows = "rows",
    title = "title",
    maxColumn = "maxColumn",
    buttonVariant = "buttonVariant",
    showAsButton = "showAsButton",
    singleButtonFilePath = "singleButtonFilePath",
    singleButtonName = "singleButtonName",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.hasMultipleFiles]: boolean;
        [fields.title]: string;
        [fields.maxColumn]: GridWidths;
        [fields.rows]: RowModel[];
        [fields.showAsButton]: boolean;
        [fields.buttonVariant]: ButtonVariants | undefined;
        [fields.singleButtonFilePath]: string;
        [fields.singleButtonName]: string;
    };
}

export interface ProductDetailsDocumentsStyles {
    mainWrapper?: InjectableCss;
    widgetTitle?: TypographyPresentationProps;
    listWrapper?: InjectableCss;
    container?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
}

export const documentsStyles: ProductDetailsDocumentsStyles = {
    mainWrapper: {
        css: css`
            width: 100%;
            ${boxStyles()}

            & > * {
                ${contentSpacingStyles(0, 0, "1em")}
            }
        `,
    },
    widgetTitle: {
        color: "primary",
        forwardAs: "h3",
        variant: "h4",
    },
    listWrapper: documentsListStyles.wrapper,
    container: documentsListStyles.container,
    documentGridItem: documentsListStyles.documentGridItem,
    documentLink: documentsListStyles.documentLink,
    documentIcon: documentsListStyles.documentIcon,
    documentNameText: documentsListStyles.documentNameText,
};

const styles = documentsStyles;

const FileList: FC<OwnProps> = ({
    id,
    fields: {
        hasMultipleFiles,
        rows,
        title,
        maxColumn,
        buttonVariant,
        showAsButton,
        singleButtonFilePath,
        singleButtonName,
    },
}) => {
    const gridItemStyle: GridItemProps = {
        ...documentsListStyles.documentGridItem,
        width: [12, 6, maxColumn, maxColumn, maxColumn],
    };

    const documentNameStyle = (ellipsis: boolean) => {
        const documentNameTextAlt = {
            ...styles.documentNameText,
            ellipsis: false,
            css: css`
                ${styles.documentNameText?.css}
                line-height: ${({ theme }) => theme.design.typography.lineHeight.small};
            `,
        };

        if (ellipsis) {
            return styles.documentNameText;
        }

        return documentNameTextAlt;
    };

    const linkOrButtonComponent = ({
        filePath,
        name,
        ellipsis = false,
    }: {
        filePath: string;
        name: string;
        ellipsis?: boolean;
    }) => {
        if (filePath) {
            return showAsButton ? (
                <Button
                    onClick={() => window.open(filePath, "_blank")}
                    variant={buttonVariant}
                    data-test-selector={`productDetails_${name}`}
                >
                    {name || filePath.split("/").pop()}
                </Button>
            ) : (
                <Link
                    href={filePath}
                    target="_new"
                    {...styles.documentLink}
                    data-test-selector={`productDetails_${name}`}
                >
                    <IconMemo {...styles.documentIcon} />
                    <Typography {...documentNameStyle(ellipsis)}>{name || filePath.split("/").pop()}</Typography>
                </Link>
            );
        }

        return null;
    };

    if (hasMultipleFiles) {
        return (
            <StyledWrapper {...styles.mainWrapper}>
                {title && <Typography {...styles.widgetTitle}>{title}</Typography>}
                <StyledWrapper {...styles.listWrapper}>
                    <GridContainer {...styles.container}>
                        {rows.map(({ fields: { filePath, name, ellipsis } }) => {
                            return (
                                filePath && (
                                    <GridItem key={name} {...gridItemStyle}>
                                        {linkOrButtonComponent({ filePath, name, ellipsis })}
                                    </GridItem>
                                )
                            );
                        })}
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    return linkOrButtonComponent({ filePath: singleButtonFilePath, name: singleButtonName });
};

const widgetModule: WidgetModule<ToolkitFieldDefinition> = {
    component: FileList,
    definition: {
        group: "Basic",
        displayName: "File List",
        fieldDefinitions: [
            {
                name: fields.hasMultipleFiles,
                displayName: "Has Multiple Files",
                editorTemplate: "CheckboxField",
                variant: "toggle",
                fieldType: "General",
                defaultValue: false,
                isRequired: false,
            },
            {
                name: fields.singleButtonFilePath,
                displayName: "File",
                editorTemplate: "FilePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
                isVisible: x => !x.fields.hasMultipleFiles,
            },
            {
                name: fields.singleButtonName,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
                isVisible: x => !x.fields.hasMultipleFiles,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
                isVisible: x => x.fields.hasMultipleFiles,
            },
            {
                name: fields.maxColumn,
                displayName: "Number of maximum columns",
                editorTemplate: "DropDownField",
                defaultValue: 6,
                isRequired: false,
                fieldType: "General",
                isVisible: x => x.fields.hasMultipleFiles,
                options: [
                    // Value is 12 / displayname
                    {
                        displayName: "2",
                        value: 6,
                    },
                    {
                        displayName: "3",
                        value: 4,
                    },
                    {
                        displayName: "4",
                        value: 3,
                    },
                ],
            },
            {
                name: fields.rows,
                displayName: "Documents",
                editorTemplate: "ListField",
                getDisplay: ({ fields: { name } }) => name,
                isVisible: x => x.fields.hasMultipleFiles,
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "filePath",
                        displayName: "File",
                        editorTemplate: "FilePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "name",
                        displayName: "Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                        isVisible: x => x.fields.filePath,
                    },
                    {
                        name: "ellipsis",
                        displayName: "Suspension points",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isRequired: false,
                        isVisible: x => x.fields.filePath,
                    },
                ],
            },
            {
                name: "showAsButton",
                displayName: "Display as button",
                editorTemplate: "CheckboxField",
                variant: "toggle",
                fieldType: "General",
                defaultValue: false,
                isRequired: false,
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                    { displayName: "Header", value: "header" },
                ],
                defaultValue: "primary",
                isVisible: x => x.fields.showAsButton,
            },
        ],
    },
};

export default widgetModule;
